import { ChevronDown, ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TablePaginationProps {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** Word after the count, e.g. "members" or "loans". */
  itemLabel?: string;
}

const PAGE_SIZES = [10, 25, 50, 100];
const MAX_VISIBLE_PAGES = 5;

const getPageNumbers = (currentPage: number, totalPages: number): (number | 'gap')[] => {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages: (number | 'gap')[] = [1];
  if (currentPage > 3) pages.push('gap');
  for (let page = Math.max(2, currentPage - 1); page <= Math.min(totalPages - 1, currentPage + 1); page++) {
    pages.push(page);
  }
  if (currentPage < totalPages - 2) pages.push('gap');
  pages.push(totalPages);
  return pages;
};

/** "Showing 1–10 of 42" + page size + page buttons, shared by the data tables. */
export function TablePagination({
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  itemLabel,
}: TablePaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const start = totalItems ? (currentPage - 1) * pageSize + 1 : 0;
  const end = Math.min(currentPage * pageSize, totalItems);
  const navButtonClass = 'h-8 w-8 border-gray-200 bg-white text-gray-600 hover:bg-gray-50';

  return (
    <div className="flex shrink-0 items-center justify-between gap-4 border-t border-gray-200 bg-white px-4 py-2.5">
      <p className="text-sm text-gray-500">
        Showing{' '}
        <span className="font-medium text-gray-700">
          {start}–{end}
        </span>{' '}
        of <span className="font-medium text-gray-700">{totalItems}</span>
        {itemLabel ? ` ${itemLabel}` : ''}
      </p>

      <div className="flex items-center gap-3">
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="table-page-size" className="text-sm text-gray-500">
              Per page
            </label>
            <div className="relative">
              <select
                id="table-page-size"
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
                className="h-8 cursor-pointer appearance-none rounded-md border border-gray-200 bg-white py-0 pl-2.5 pr-7 text-sm text-gray-700 outline-none focus:border-gray-300"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={navButtonClass}
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {getPageNumbers(currentPage, totalPages).map((page, index) =>
              page === 'gap' ? (
                <span key={`gap-${index}`} className="flex h-8 w-8 items-center justify-center text-gray-400" aria-hidden>
                  <MoreHorizontal className="h-4 w-4" />
                </span>
              ) : (
                <Button
                  key={page}
                  type="button"
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-8 min-w-8 px-2.5 text-sm',
                    currentPage === page
                      ? 'border-[#4369a0]/30 bg-[#4369a0]/10 font-medium text-[#4369a0] hover:bg-[#4369a0]/15'
                      : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                  )}
                  onClick={() => onPageChange(page)}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </Button>
              )
            )}

            <Button
              type="button"
              variant="outline"
              size="icon"
              className={navButtonClass}
              disabled={currentPage === totalPages}
              onClick={() => onPageChange(currentPage + 1)}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
